import React, { useCallback, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { CardPix, ListItemPix } from '@/components'
import { ApiService, BridgeService } from '@/services'
import { Spacing } from '@interco/inter-ui/components/Spacing'
import { Text } from '@interco/inter-ui/components/Text'
import { Button } from '@interco/inter-ui/components/Button'
import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'
import { useSnackBar } from '@interco/inter-ui/components/SnackBar'
import useGlobalState from '@/contexts/global-state'
import { ENUM_PIX_TYPE } from '@/common/constants/pix-type'
import IconCheck from '@inter/icons/orangeds/LG/check'
import { WbSessionActions } from '@interco/inter-webview-bridge'
import { setPixKey, setUser } from '@/contexts/global-state/reducer/actions'
import { BaseContainer } from '@/components/base-container'
import { RoutesNames } from '@/routes/routes'
import { eventTag1, eventTag2, eventTag3, eventTag4 } from '@/utils/tagEvent'
import { formatPixDocumentKey } from '@/utils/pix-document'

import * as S from './styles'

export const Home = () => {
  const { showSnackBar } = useSnackBar()
  const navigate = useNavigate()
  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false)
  const [selectedPixKey, setSelectedPixKey] = useState<PixItem | undefined>()

  const [loading, setLoading] = useState<boolean>(true)

  const [{ pix, intl }] = useGlobalState()
  const { hasManyPix, pixList, pixHierarchy } = pix

  const [{ user }, dispatch] = useGlobalState()

  const onHeaderBackPress = useCallback(() => {
    BridgeService.interWbNavigate.requestGoBack()
  }, [])

  const getUserInfo = useCallback(async () => {
    if (user) return
    const response = await BridgeService.getUserInfo()

    dispatch(
      setUser({
        user: {
          ...response,
          cpf: response.cpf,
        },
      }),
    )
  }, [dispatch, user])
  const handleCopyKeyPix = ({
    pixKey,
    closeBottonSheet,
    tagEvent,
  }: {
    pixKey: PixItem
    closeBottonSheet?: boolean
    tagEvent: (keyType: PixType) => void
  }) => {
    tagEvent(pixKey.keyType)
    navigator.clipboard.writeText(pixKey.key)
    showSnackBar({
      title: intl.copyKeyToast,
      content: '',
      type: 'DEFAULT',
      hasProgressBar: true,
      customIcon: <IconCheck height={24} width={24} />,
    })

    if (closeBottonSheet) {
      setShowBottomSheet(false)
    }
  }

  const getPix = useCallback(async () => {
    setLoading(true)

    try {
      const response = await ApiService.Pix.getPix()
      if (!response) {
        navigate(RoutesNames.ERRO)
      } else {
        const { pixKeys } = response

        const hierarchy: Record<string, number> = { CPF: 0, CNPJ: 1, PHONE: 2, EMAIL: 3, EVP: 4 }
        const pixKeyordered = pixKeys.sort((a, b) => hierarchy[a.keyType] - hierarchy[b.keyType])

        const responseData: PixFormated = {
          hasManyPix: pixKeys.length > 1,
          pixHierarchy: pixKeyordered[0],
          pixList: pixKeyordered,
        }
        dispatch(setPixKey({ pix: responseData }))
        eventTag1()
      }
    } catch {
      navigate(RoutesNames.ERRO)
    } finally {
      setLoading(false)
    }
  }, [dispatch, navigate])

  const onMount = useCallback(() => {
    const callStack = async () => {
      if (BridgeService.inApp) {
        await getUserInfo()
        if (user) await getPix()
      } else {
        await getPix()
      }
    }

    callStack()
  }, [getPix, getUserInfo, user])

  useEffect(() => {
    BridgeService.iWb.addWebviewEventListener(WbSessionActions.GO_BACK, onHeaderBackPress)
  }, [onHeaderBackPress])

  useEffect(() => {
    onMount()
  }, [onMount])

  useEffect(() => {
    if (pixHierarchy?.key) {
      setSelectedPixKey(pixHierarchy)
    }
  }, [pixHierarchy])

  const handleShowOtherKeyPix = () => {
    eventTag4()
    setShowBottomSheet(true)
  }

  const goBack = useCallback(() => {
    BridgeService.interWbNavigate.requestGoBack()
  }, [])

  const getFormattedPixKey = useCallback((pixKey?: PixItem) => {
    if (!pixKey?.key) {
      return ''
    }

    if (pixKey.keyType !== 'CPF' && pixKey.keyType !== 'CNPJ') {
      return pixKey.key
    }

    return formatPixDocumentKey({
      key: pixKey.key,
      keyType: pixKey.keyType,
    })
  }, [])

  const currentPixKey = selectedPixKey ?? pixHierarchy

  return (
    <BaseContainer onClickArrowLeft={goBack} headerTitle="" animationType="fade">
      <S.Container>
        <S.Content>
          <Spacing mb="sm" />
          <Text variant="headline-h1" colorWeight={500} semiBold>
            {intl.firstDeposit}
          </Text>
          <Spacing mb="xs" />
          <Text variant="body-2">{intl.useYourPixkey}</Text>
          <Spacing mb="sm" />

          <CardPix
            isLoading={loading}
            title={loading ? '' : ENUM_PIX_TYPE[currentPixKey.keyType]}
            keyPix={loading ? '' : getFormattedPixKey(currentPixKey)}
            onCopyKey={() => handleCopyKeyPix({ pixKey: currentPixKey, tagEvent: eventTag2 })}
          />
        </S.Content>
        {!loading && (
          <S.Actions>
            {currentPixKey.wasCreatedNow && (
              <>
                <Text variant="body-3">{intl.wasCreatedYourkey}</Text>
                <Spacing mb="sm" />
              </>
            )}

            <Button
              onClick={() => handleCopyKeyPix({ pixKey: currentPixKey, tagEvent: eventTag3 })}
            >
              {intl.btnCopyKey}
            </Button>

            {hasManyPix && (
              <>
                <Spacing mb="xs" />
                <Button variant="secondary" onClick={handleShowOtherKeyPix}>
                  {intl.btnUseAnotherKey}
                </Button>
              </>
            )}
          </S.Actions>
        )}

        {showBottomSheet && hasManyPix && (
          <BottomSheet onClose={() => setShowBottomSheet(false)}>
            <S.ListPix>
              {pixList.map((item) => (
                <ListItemPix
                  key={item.key}
                  title={item.keyType}
                  keyPix={getFormattedPixKey(item)}
                  onCopyKey={() => {
                    setSelectedPixKey(item)
                    handleCopyKeyPix({ pixKey: item, tagEvent: eventTag2, closeBottonSheet: true })
                  }}
                />
              ))}
            </S.ListPix>
          </BottomSheet>
        )}
      </S.Container>
    </BaseContainer>
  )
}
